import { formatMoney } from '@theme/utilities';
import { ToggleCrossSellsUpdatedEvent } from '@theme/events';

class ToggleCrossSellList extends HTMLElement {
  constructor() {
    super();

    this.crossSells = null;
  }

  connectedCallback() {
    this.crossSells = this.querySelectorAll('[data-ref="toggle-cross-sell"]');
  }

  getSelectedItems() {
    let items = [];
    let total_price = 0;
    let total_compare_at_price = 0;
    let total_quantity = 0;

    this.crossSells.forEach((crossSell) => {
      const checkbox = crossSell.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
      const button = crossSell.querySelector('[data-ref="toggle-cross-sell-button"]');

      if (checkbox != null && checkbox.checked == true) {
        items.push({
          id: checkbox.dataset.variantId,
          quantity: 1,
        });

        total_quantity += 1;
        total_price += parseInt(crossSell.getPrices().price);
        total_compare_at_price += parseInt(crossSell.getPrices().compareAtPrice);
      }

      if (button != null) {
        const quantityInput = crossSell.querySelector('[data-ref="toggle-cross-sell-input"]');
        const quantity = parseInt(quantityInput?.value, 10) || 0;

        if (quantity <= 0) return;

        items.push({
          id: button.dataset.variantId,
          quantity: quantity,
        });

        total_quantity += quantity;
        total_price += parseInt(crossSell.getPrices().price) * quantity;
        total_compare_at_price += parseInt(crossSell.getPrices().compareAtPrice) * quantity;
      }
    });

    return { items: items, total_price: total_price, total_compare_at_price: total_compare_at_price, total_quantity: total_quantity };
  }

  getSubscriptionPrices() {
    if (this.dataset.includeInSubscriptionPricing !== 'true') {
      return { total_price: 0, total_compare_at_price: 0, total_quantity: 0 };
    }

    const { total_price, total_compare_at_price, total_quantity } = this.getSelectedItems();

    return { total_price, total_compare_at_price, total_quantity };
  }

  getCartItems(sellingPlan = '') {
    const { items } = this.getSelectedItems();

    if (this.dataset.includeInSubscriptionPricing !== 'true' || !sellingPlan) {
      return items;
    }

    return items.map((item) => ({
      ...item,
      selling_plan: sellingPlan,
    }));
  }
}

if (!customElements.get('toggle-cross-sell-list')) {
  customElements.define('toggle-cross-sell-list', ToggleCrossSellList);
}

class ToggleCrossSell extends HTMLElement {
  constructor() {
    super();

    this.checkbox = null;
    this.button = null;
    this.quantityContainer = null;
    this.minusButton = null;
    this.plusButton = null;
    this.quantityInput = null;
    this.select = null;
    this.image = null;
    this.price = null;
    this.defaultImageSrc = '';

    this.handleVariantChange = this.variantChanged.bind(this);
    this.handleAddClick = () => this.setQuantity(1);
    this.handleMinusClick = () => this.setQuantity((parseInt(this.quantityInput?.value, 10) || 0) - 1);
    this.handlePlusClick = () => this.setQuantity((parseInt(this.quantityInput?.value, 10) || 0) + 1);
    this.handleQuantityChange = () => this.setQuantity(parseInt(this.quantityInput?.value, 10) || 0);
    this.handleCheckboxChange = this.checkboxChanged.bind(this);
  }

  connectedCallback() {
    this.checkbox = this.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
    this.button = this.querySelector('[data-ref="toggle-cross-sell-button"]');
    this.quantityContainer = this.querySelector('[data-ref="toggle-cross-sell-quantity"]');
    this.minusButton = this.querySelector('[data-ref="toggle-cross-sell-minus"]');
    this.plusButton = this.querySelector('[data-ref="toggle-cross-sell-plus"]');
    this.quantityInput = this.querySelector('[data-ref="toggle-cross-sell-input"]');
    this.select = this.querySelector('[data-ref="toggle-cross-sell-select"]');
    this.image = this.querySelector('[data-ref="toggle-cross-sell-image"]');
    this.price = this.querySelector('[data-ref="toggle-cross-sell-price"]');
    this.defaultImageSrc = this.image?.querySelector('img')?.getAttribute('src') || '';

    this.select?.addEventListener('change', this.handleVariantChange);
    this.button?.addEventListener('click', this.handleAddClick);
    this.minusButton?.addEventListener('click', this.handleMinusClick);
    this.plusButton?.addEventListener('click', this.handlePlusClick);
    this.quantityInput?.addEventListener('change', this.handleQuantityChange);
    this.checkbox?.addEventListener('change', this.handleCheckboxChange);

    this.setQuantity(parseInt(this.quantityInput?.value, 10) || 0, false);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.handleVariantChange);
    this.button?.removeEventListener('click', this.handleAddClick);
    this.minusButton?.removeEventListener('click', this.handleMinusClick);
    this.plusButton?.removeEventListener('click', this.handlePlusClick);
    this.quantityInput?.removeEventListener('change', this.handleQuantityChange);
    this.checkbox?.removeEventListener('change', this.handleCheckboxChange);
  }

  checkboxChanged() {
    this.#dispatchUpdate();
  }

  setQuantity(quantity, dispatchEvent = true) {
    const nextQuantity = Math.max(0, quantity);

    if (this.quantityInput) {
      this.quantityInput.value = String(nextQuantity);
    }

    if (this.button) {
      this.button.hidden = nextQuantity > 0;
    }

    if (this.quantityContainer) {
      this.quantityContainer.hidden = nextQuantity <= 0;
    }

    if (dispatchEvent) {
      this.#dispatchUpdate();
    }
  }

  variantChanged() {
    if (!this.select) return;

    const optionSelected = this.select.options[this.select.selectedIndex];

    this.updateProductPrice(optionSelected);
    this.updateProductImage(optionSelected);
    this.updateVariantId(optionSelected);

    if (this.checkbox != null) this.checkbox.checked = true;
    if (this.button != null && !this.quantityContainer?.hidden) this.setQuantity(parseInt(this.quantityInput?.value, 10) || 1);

    this.#dispatchUpdate();
  }

  updateProductPrice(optionSelected) {
    if (!this.price) return;

    const variantPrice = optionSelected.dataset.price;
    const variantCompareAtPrice = optionSelected.dataset.compareAtPrice;
    const priceElement = this.price.querySelector('.price');
    const compareAtPriceElement = this.price.querySelector('.compare-at-price');

    if (priceElement) {
      priceElement.innerHTML = formatMoney(variantPrice);
    }
    this.dataset.price = variantPrice;

    if (compareAtPriceElement) {
      compareAtPriceElement.innerHTML = '';
    }

    if (variantCompareAtPrice != null) {
      if (compareAtPriceElement) {
        compareAtPriceElement.innerHTML = formatMoney(variantCompareAtPrice);
      }
      this.dataset.compareAtPrice = variantCompareAtPrice;
      return;
    }

    delete this.dataset.compareAtPrice;
  }

  updateProductImage(optionSelected) {
    if (!this.image) return;

    const variantImage = optionSelected.dataset.variantImage;
    const image = this.image.querySelector('img');

    if (!image) return;

    if (variantImage) {
      image.src = variantImage;
      return;
    }

    if (this.defaultImageSrc) {
      image.src = this.defaultImageSrc;
    }
  }

  updateVariantId(optionSelected) {
    const variantId = optionSelected.value;

    if (this.checkbox != null) this.checkbox.dataset.variantId = variantId;
    if (this.button != null) this.button.dataset.variantId = variantId;
    if (this.minusButton != null) this.minusButton.dataset.variantId = variantId;
    if (this.plusButton != null) this.plusButton.dataset.variantId = variantId;
    if (this.quantityInput != null) this.quantityInput.dataset.variantId = variantId;
  }

  getPrices() {
    let price = 0;
    let compareAtPrice = 0;

    if (this.dataset.price != null) {
      price = parseInt(this.dataset.price);
    }

    if (this.dataset.compareAtPrice != null) {
      compareAtPrice = parseInt(this.dataset.compareAtPrice);
    } else {
      compareAtPrice = parseInt(this.dataset.price);
    }

    return {
      price: price,
      compareAtPrice: compareAtPrice,
    };
  }

  #dispatchUpdate() {
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }
}

if (!customElements.get('toggle-cross-sell')) {
  customElements.define('toggle-cross-sell', ToggleCrossSell);
}
